import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { User, Clock, Trophy, Volume2, VolumeX, Search, Check, X, ChevronDown, MessageCircle, RefreshCw } from 'lucide-react';
import { checkRankUp } from '../utils/points';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const apiClient = axios.create({
  baseURL: API,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const DuelMode = ({ user, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Main navigation state
  const [activeTab, setActiveTab] = useState('lobby');
  const [hoveredTab, setHoveredTab] = useState(null);
  
  // Challenge form state
  const [showChallengeForm, setShowChallengeForm] = useState(false);
  const [challengeUsername, setChallengeUsername] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeUsers, setActiveUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [showUserList, setShowUserList] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [challengerMessage, setChallengerMessage] = useState(''); // Message from challenger
  const [duelTopic, setDuelTopic] = useState(''); // '' = General, or specific topic
  
  // Duel lists
  const [pendingDuels, setPendingDuels] = useState([]);
  const [completedDuels, setCompletedDuels] = useState([]);
  
  // ==================== GAME STATE ====================
  const [gameMode, setGameMode] = useState(null); // null, 'playing'
  const [currentDuel, setCurrentDuel] = useState(null);
  const [currentRound, setCurrentRound] = useState(0); // 0-4 for 5 questions
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [myAnswers, setMyAnswers] = useState([]);
  const [receivedChallengerMessage, setReceivedChallengerMessage] = useState(null); // Message received from challenger
  const [winnerMessage, setWinnerMessage] = useState(''); // Message to send when winning
  const [showWinnerMessageInput, setShowWinnerMessageInput] = useState(false);
  const [myFinalScore, setMyFinalScore] = useState(null);
  const [gamePhase, setGamePhase] = useState('wheel'); // wheel, question, feedback, results
  
  // Opponent info for game display
  const [opponentInfo, setOpponentInfo] = useState(null);
  
  // Refs for game state to avoid closure issues
  const currentDuelRef = useRef(null);
  const currentRoundRef = useRef(0);
  const gameModeRef = useRef(null);
  
  // ==================== WHEEL STATE ====================
  const [wheelRotation, setWheelRotation] = useState(0);
  const [isSpinning, setIsSpinning] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [selectedSpecialty, setSelectedSpecialty] = useState(null);
  
  // Refs for wheel animation
  const wheelRef = useRef(null);
  const pointerRef = useRef(null);
  const animationRef = useRef(null);
  const audioContextRef = useRef(null);
  const lastSectionIndexRef = useRef(-1);
  const velocityRef = useRef(0);
  const rotationRef = useRef(0);
  const soundEnabledRef = useRef(true);
  
  // Wheel configuration - materias are fetched from backend (canonical fixed list)
  const [materias, setMaterias] = useState([]);
  const specialties = useMemo(() => {
    const n = materias.length || 1;
    return materias.map((m, i) => ({
      name: m.label,
      value: m.value,
      specialty: m.specialty,
      color: `hsl(${Math.round((i * 360) / n)}, 68%, 54%)`
    }));
  }, [materias]);
  const SEGMENT_ANGLE = useMemo(() => (specialties.length ? 360 / specialties.length : 72), [specialties]);
  const FRICTION = 0.985;

  useEffect(() => {
    apiClient.get('/duels/materias')
      .then(res => setMaterias(res.data?.materias || []))
      .catch(() => setMaterias([]));
  }, []);

  // Keep refs in sync with state
  useEffect(() => {
    currentDuelRef.current = currentDuel;
  }, [currentDuel]);
  
  useEffect(() => {
    currentRoundRef.current = currentRound;
  }, [currentRound]);
  
  useEffect(() => {
    gameModeRef.current = gameMode;
  }, [gameMode]);

  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
  }, [soundEnabled]);

  useEffect(() => {
    // Load data only once when component mounts
    fetchData(true);
    // No polling - user can manually refresh with button
    // This reduces server load significantly
  }, []);

  // No need to refresh on tab change - data is already loaded

  // Handle startDuel URL parameter (when user clicks notification)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const startDuelId = params.get('startDuel');
    
    if (startDuelId && !gameMode) {
      // Start the duel directly
      startDuelFromNotification(startDuelId);
      // Clean the URL
      navigate('/duels', { replace: true });
    }
  }, [location.search, gameMode, navigate]);

  const startDuelFromNotification = async (duelId) => {
    try {
      const duelResponse = await apiClient.get(`/duels/${duelId}`);
      const duelData = duelResponse.data;
      
      // Get challenger info
      const challenger = await apiClient.get(`/users/profile/${duelData.player1_id}`).catch(() => null);
      
      setCurrentDuel(duelData);
      setOpponentInfo({
        id: duelData.player1_id,
        name: challenger?.data?.full_name || 'Retador',
        image: challenger?.data?.profile_image
      });
      
      setGameMode('playing');
      setGamePhase('wheel');
      setCurrentRound(0);
      setMyAnswers([]);
      
      toast.success('¡Comenzando duelo!');
    } catch (error) {
      toast.error('Error al cargar el duelo');
      console.error('Error starting duel from notification:', error);
    }
  };

  // Cleanup audio context on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  const fetchData = async (showLoading = false) => {
    if (showLoading) setLoadingData(true);
    try {
      const [pendingResponse, completedResponse] = await Promise.allSettled([
        apiClient.get('/duels/pending'),
        apiClient.get('/duels/completed?page=1&page_size=50')
      ]);
      
      // Only update if we got successful responses - don't clear data on error
      if (pendingResponse.status === 'fulfilled') {
        const pendingData = pendingResponse.value.data || [];
        setPendingDuels(pendingData);
        console.log('Retos pendientes cargados:', pendingData.length);
      } else {
        console.error('Error cargando retos pendientes:', pendingResponse.reason);
      }
      
      if (completedResponse.status === 'fulfilled') {
        // Handle new paginated response format
        const responseData = completedResponse.value.data;
        const completedData = responseData?.duels || responseData || [];
        setCompletedDuels(completedData);
        console.log('Duelos terminados cargados:', completedData.length);
      } else {
        console.error('Error cargando duelos terminados:', completedResponse.reason);
      }
    } catch (error) {
      console.error('Error fetching duel data:', error);
      // Don't clear existing data on error - keep what we have
    } finally {
      if (showLoading) setLoadingData(false);
    }
  };

  const fetchActiveUsers = async (searchQuery = '') => {
    setLoadingUsers(true);
    try {
      // Use search endpoint with query for scalability
      const endpoint = searchQuery.length >= 2 
        ? `/users/search?q=${encodeURIComponent(searchQuery)}&limit=1000`
        : '/users/active?limit=1000';
      const response = await apiClient.get(endpoint);
      const users = response.data || [];
      setActiveUsers(users);
      setFilteredUsers(users);
      console.log('Usuarios cargados:', users.length);
    } catch (error) {
      console.error('Error fetching active users:', error);
      // Don't show error toast for every search
    } finally {
      setLoadingUsers(false);
    }
  };

  // Debounce search to avoid too many API calls
  const searchTimeoutRef = useRef(null);
  
  const filterUsers = (query) => {
    setChallengeUsername(query);
    setShowUserList(true);
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    if (query.length < 2) {
      // Show initial list if query is too short
      if (activeUsers.length === 0) {
        fetchActiveUsers('');
      } else {
        setFilteredUsers(activeUsers);
      }
      return;
    }
    
    // Debounce API call by 300ms
    searchTimeoutRef.current = setTimeout(() => {
      fetchActiveUsers(query);
    }, 300);
  };

  const selectUser = (userItem) => {
    setSelectedUser(userItem);
    setChallengeUsername(userItem.full_name);
    setShowUserList(false);
  };

  // ==================== WHEEL PHYSICS ====================
  
  const playTick = useCallback(() => {
    if (!soundEnabledRef.current) return;
    
    try {
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioContextRef.current;
      const t = ctx.currentTime;

      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.type = 'triangle';
      osc.frequency.setValueAtTime(300, t);
      osc.frequency.exponentialRampToValueAtTime(50, t + 0.05);

      gain.gain.setValueAtTime(0.15, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.05);

      osc.start(t);
      osc.stop(t + 0.05);
    } catch (e) {
      console.log('Audio tick failed:', e);
    }
  }, []);

  const triggerTick = useCallback(() => {
    if (pointerRef.current) {
      pointerRef.current.style.transform = 'translateX(-50%) rotate(25deg)';
      setTimeout(() => {
        if (pointerRef.current) {
          pointerRef.current.style.transform = 'translateX(-50%) rotate(0deg)';
        }
      }, 100);
    }
    playTick();
  }, [playTick]);

  // ==================== GAME LOGIC ====================
  
  const loadQuestionForRound = useCallback(async () => {
    const duel = currentDuelRef.current;
    const round = currentRoundRef.current;
    
    if (!duel || round >= 5) return;
    
    try {
      const questionId = duel.round_questions[round];
      const source = duel.question_source || 'gpc';
      const endpoint = source === 'duel' ? `/duel-questions/${questionId}` : `/questions/${questionId}`;
      const response = await apiClient.get(endpoint);
      setCurrentQuestion(response.data);
      setGamePhase('question');
    } catch (error) {
      toast.error('Error al cargar pregunta');
      console.error('Error loading question:', error);
    }
  }, []);

  // Use a ref for loadQuestionForRound to avoid circular dependencies
  const loadQuestionForRoundRef = useRef(loadQuestionForRound);
  useEffect(() => {
    loadQuestionForRoundRef.current = loadQuestionForRound;
  }, [loadQuestionForRound]);

  const wheelLoop = useCallback(() => {
    rotationRef.current += velocityRef.current;
    velocityRef.current *= FRICTION;
    
    setWheelRotation(rotationRef.current);
    
    const normRot = ((rotationRef.current % 360) + 360) % 360;
    const pegIndex = Math.floor(normRot / SEGMENT_ANGLE);
    
    if (pegIndex !== lastSectionIndexRef.current && velocityRef.current > 0.5) {
      triggerTick();
      lastSectionIndexRef.current = pegIndex;
    }
    
    if (Math.abs(velocityRef.current) < 0.1) {
      velocityRef.current = 0;
      setIsSpinning(false);
      
      // Calculate which segment is under the pointer (at top, 0 degrees)
      // The wheel rotates clockwise. When rotation = R, the segment originally at angle (360 - R) is now at top
      const finalNormRot = ((rotationRef.current % 360) + 360) % 360;
      const angleAtPointer = (360 - finalNormRot + 360) % 360;
      const segmentIndex = Math.floor(angleAtPointer / SEGMENT_ANGLE) % specialties.length;
      const selected = specialties[segmentIndex];
      setSelectedSpecialty(selected);
      toast.success(`¡${selected.name}!`);
      
      // After wheel stops, load the question for this round
      if (gameModeRef.current === 'playing' && currentDuelRef.current) {
        setTimeout(() => {
          loadQuestionForRoundRef.current();
        }, 1500);
      }
      return;
    }
    
    animationRef.current = requestAnimationFrame(wheelLoop);
  }, [triggerTick, specialties, SEGMENT_ANGLE, FRICTION]);

  const spinWheel = () => {
    // Block spinning if not in wheel phase or already spinning
    if (isSpinning || gamePhase !== 'wheel') return;
    
    // Get the pre-generated specialty for this round
    const targetSpecialty = currentDuelRef.current?.round_specialties?.[currentRoundRef.current];
    
    // Find the index of the target specialty
    let targetIndex = specialties.findIndex(s => s.name === targetSpecialty);
    if (targetIndex === -1) targetIndex = 0; // Fallback
    
    // Calculate where the pointer currently points
    // When wheel is at rotation R, the segment under the pointer (at top) is:
    // segmentIndex = floor(((360 - (R % 360)) % 360) / SEGMENT_ANGLE)
    // We want to rotate to have targetIndex under the pointer
    // Target position for segment i to be at top: rotation where (360 - R) % 360 = i * 72 + 36 (center)
    // So: R = 360 - (i * 72 + 36) = 324 - i * 72
    // But we need to account for current rotation and add full spins
    
    const targetAngleFromTop = targetIndex * SEGMENT_ANGLE + (SEGMENT_ANGLE / 2); // Center of target segment from 0°
    const rotationNeeded = (360 - targetAngleFromTop + 360) % 360; // Rotation to put target at top
    
    // Normalize current rotation to 0-360
    const currentNormalized = ((rotationRef.current % 360) + 360) % 360;
    
    // Calculate additional rotation needed from current position
    let additionalRotation = (rotationNeeded - currentNormalized + 360) % 360;
    if (additionalRotation < SEGMENT_ANGLE) additionalRotation += 360; // Ensure at least some visible spin
    
    // Add multiple full rotations for visual effect
    const isFixedTopic = currentDuelRef.current?.duel_topic;
    const fullRotations = isFixedTopic 
      ? (3 + Math.floor(Math.random() * 2)) * 360  // 3-4 spins for fixed topic (shorter)
      : (6 + Math.floor(Math.random() * 3)) * 360; // 6-9 spins for general
    // Small random offset for natural feel, kept well within the target segment
    // (max ±30% of a segment so the pointer never crosses into an adjacent one)
    const randomOffset = (Math.random() - 0.5) * (SEGMENT_ANGLE * 0.6);
    const finalTargetRotation = rotationRef.current + fullRotations + additionalRotation + randomOffset;
    
    // Set target rotation and start animation
    targetRotationRef.current = finalTargetRotation;
    initialRotationRef.current = rotationRef.current;
    spinStartTimeRef.current = performance.now();
    spinDurationRef.current = isFixedTopic 
      ? 2000 + Math.random() * 1000  // 2-3 seconds for fixed topic
      : 4000 + Math.random() * 2000; // 4-6 seconds for general
    lastSectionIndexRef.current = -1;
    setIsSpinning(true);
    setSelectedSpecialty(null);
    
    console.log(`Target: ${targetSpecialty} (idx ${targetIndex}), targetAngle: ${targetAngleFromTop}°, rotationNeeded: ${rotationNeeded}°, additional: ${additionalRotation}°, final: ${finalTargetRotation}°`);
    
    animationRef.current = requestAnimationFrame(wheelLoopTargeted);
  };
  
  // Refs for wheel animation
  const targetRotationRef = useRef(0);
  const initialRotationRef = useRef(0);
  const spinStartTimeRef = useRef(0);
  const spinDurationRef = useRef(4000);
  
  // Easing function: ease out cubic (starts fast, slows down)
  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
  
  // New wheel loop with proper easing animation
  const wheelLoopTargeted = useCallback(() => {
    const now = performance.now();
    const elapsed = now - spinStartTimeRef.current;
    const duration = spinDurationRef.current;
    const progress = Math.min(elapsed / duration, 1);
    
    // Use easing function for smooth deceleration
    const easedProgress = easeOutCubic(progress);
    
    // Calculate current rotation
    const totalRotation = targetRotationRef.current - initialRotationRef.current;
    const currentRotation = initialRotationRef.current + (totalRotation * easedProgress);
    
    rotationRef.current = currentRotation;
    setWheelRotation(currentRotation);
    
    // Calculate current velocity for tick sound timing
    const instantVelocity = progress < 1 ? (1 - progress) * 30 : 0;
    velocityRef.current = instantVelocity;
    
    // Detect section crossing for tick sound
    const normRot = ((currentRotation % 360) + 360) % 360;
    const pegIndex = Math.floor(normRot / SEGMENT_ANGLE);
    
    if (pegIndex !== lastSectionIndexRef.current && instantVelocity > 2) {
      triggerTick();
      lastSectionIndexRef.current = pegIndex;
    }
    
    if (progress < 1) {
      animationRef.current = requestAnimationFrame(wheelLoopTargeted);
    } else {
      // Animation complete
      velocityRef.current = 0;
      setIsSpinning(false);
      
      // Get the pre-generated specialty
      const targetSpecialty = currentDuelRef.current?.round_specialties?.[currentRoundRef.current];
      const selected = specialties.find(s => s.name === targetSpecialty) || specialties[0];
      setSelectedSpecialty(selected);
      toast.success(`¡${selected.name}!`);
      
      // After wheel stops, load the question for this round
      if (gameModeRef.current === 'playing' && currentDuelRef.current) {
        setTimeout(() => {
          loadQuestionForRoundRef.current();
        }, 1500);
      }
    }
  }, [triggerTick, specialties, SEGMENT_ANGLE]);

  const handleAnswerSelect = (answer) => {
    setSelectedAnswer(answer);
    setShowFeedback(true);
    setGamePhase('feedback');
  };

  const handleNextRound = () => {
    const newAnswers = [...myAnswers, selectedAnswer];
    setMyAnswers(newAnswers);
    
    setSelectedAnswer(null);
    setShowFeedback(false);
    setCurrentQuestion(null);
    setSelectedSpecialty(null);
    
    const nextRound = currentRound + 1;
    setCurrentRound(nextRound);
    
    if (nextRound < 5) {
      setGamePhase('wheel');
    } else {
      submitGame(newAnswers);
    }
  };

  const [finalResult, setFinalResult] = useState(null); // 'win', 'lose', 'tie', null
  const [opponentFinalScore, setOpponentFinalScore] = useState(null);

  const submitGame = async (finalAnswers) => {
    try {
      const response = await apiClient.post('/duels/submit-game', {
        duel_id: currentDuel.id,
        answers: finalAnswers
      });

      setMyFinalScore(response.data.my_score);
      checkRankUp(response.data);
      
      // Check if the duel is now complete (both players finished)
      if (response.data.duel_completed) {
        // Both players have finished - show final result
        const myScore = response.data.my_score;
        const theirScore = response.data.opponent_score;
        setOpponentFinalScore(theirScore);
        
        if (myScore > theirScore) {
          setFinalResult('win');
        } else if (myScore < theirScore) {
          setFinalResult('lose');
        } else {
          setFinalResult('tie');
        }
        toast.success('¡Duelo completado!');
      } else {
        toast.success(`¡Completaste las 5 preguntas! Tu puntuación: ${response.data.my_score}/5`);
      }
      
      setGamePhase('results');
      
      // Refresh data immediately so lists update
      fetchData();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar el juego');
    }
  };

  const exitGame = () => {
    setGameMode(null);
    setCurrentDuel(null);
    setCurrentRound(0);
    setCurrentQuestion(null);
    setSelectedAnswer(null);
    setShowFeedback(false);
    setMyAnswers([]);
    setMyFinalScore(null);
    setGamePhase('wheel');
    setOpponentInfo(null);
    setSelectedSpecialty(null);
    setWheelRotation(0);
    setFinalResult(null);
    setOpponentFinalScore(null);
    setReceivedChallengerMessage(null);
    setWinnerMessage('');
    setShowWinnerMessageInput(false);
    velocityRef.current = 0;
    rotationRef.current = 0;
    fetchData();
  };

  const sendWinnerMessage = async () => {
    if (!winnerMessage.trim()) {
      toast.error('Escribe un mensaje');
      return;
    }
    
    try {
      await apiClient.post(`/duels/${currentDuel.id}/winner-message`, {
        message: winnerMessage.trim()
      });
      toast.success('¡Mensaje enviado!');
      setShowWinnerMessageInput(false);
    } catch (error) {
      toast.error('Error al enviar mensaje');
    }
  };

  // ==================== CHALLENGE ACTIONS ====================
  
  const sendChallenge = async () => {
    if (!selectedUser) {
      toast.error('Por favor selecciona un usuario');
      return;
    }

    console.log('Enviando reto a:', selectedUser.email);
    setLoading(true);
    
    try {
      const response = await apiClient.post('/duels/challenge', { 
        player2_email: selectedUser.email,
        challenger_message: challengerMessage.trim() || null,
        duel_topic: duelTopic || null
      });
      
      console.log('Duelo creado:', response.data);
      toast.success('¡Duelo creado! Comenzando juego...');
      
      // Fetch the created duel data
      const duelResponse = await apiClient.get(`/duels/${response.data.duel_id}`);
      const duelData = duelResponse.data;
      console.log('Datos del duelo:', duelData);
      
      // Set up game state
      setCurrentDuel(duelData);
      setOpponentInfo({
        id: selectedUser.id,
        name: selectedUser.full_name,
        email: selectedUser.email,
        image: selectedUser.profile_image
      });
      
      // Reset form and start game
      setShowChallengeForm(false);
      setChallengeUsername('');
      setSelectedUser(null);
      setChallengerMessage('');
      setDuelTopic('');
      setLoading(false);
      
      // Start game mode
      setGameMode('playing');
      setGamePhase('wheel');
      setCurrentRound(0);
      setMyAnswers([]);
      
    } catch (error) {
      console.error('Error al crear duelo:', error);
      toast.error(error.response?.data?.detail || 'Error al crear duelo');
      setLoading(false);
    }
  };

  const acceptDuel = async (duel) => {
    try {
      // Fetch full duel data
      const duelResponse = await apiClient.get(`/duels/${duel.id}`);
      const duelData = duelResponse.data;
      
      // Set up game state
      setCurrentDuel(duelData);
      setOpponentInfo({
        id: duel.challenger_id,
        name: duel.challenger_name,
        image: duel.challenger_image
      });
      
      // Set received challenger message if any
      if (duel.challenger_message) {
        setReceivedChallengerMessage(duel.challenger_message);
      }
      
      // Start game mode
      setGameMode('playing');
      setGamePhase('wheel');
      setCurrentRound(0);
      setMyAnswers([]);
      
      toast.success('¡Comenzando duelo!');
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar duelo');
    }
  };

  const rejectDuel = async (duelId) => {
    try {
      await apiClient.post(`/duels/reject/${duelId}`);
      toast.success('Duelo rechazado');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al rechazar duelo');
    }
  };

  const resetChallengeForm = () => {
    setShowChallengeForm(false);
    setChallengeUsername('');
    setSelectedUser(null);
    setDuelTopic('');
  };

  const tabs = [
    { id: 'lobby', label: 'Lobby', icon: '👥' },
    { id: 'pending', label: `Retos Pendientes (${pendingDuels.length})`, icon: '⏳' },
    { id: 'completed', label: `Duelos Terminados (${completedDuels.length})`, icon: '🏆' }
  ];

  // ==================== GAME UI ====================
  
  if (gameMode === 'playing' && currentDuel) {
    const isPlayer1 = user.id === currentDuel.player1_id;
    const myName = user.full_name;
    const myImage = user.profile_image;
    const opponentName = opponentInfo?.name || (isPlayer1 ? currentDuel.player2_name : currentDuel.player1_name);
    const opponentImage = opponentInfo?.image || (isPlayer1 ? currentDuel.player2_image : currentDuel.player1_image);
    
    return (
      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 py-4 px-4 z-50 bg-white border-b">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <button
              onClick={exitGame}
              className="flex items-center space-x-2 text-black font-medium text-sm hover:text-gray-600 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>Salir del Duelo</span>
            </button>
            
            <h1 className="text-lg font-bold text-black">
              Ronda {currentRound + 1} de 5
              {currentDuel?.duel_topic && (
                <span className="block text-xs font-normal text-gray-500">{currentDuel.duel_topic}</span>
              )}
            </h1>
            
            <button
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              {soundEnabled ? <Volume2 size={20} /> : <VolumeX size={20} />}
            </button>
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-4 pt-24 pb-8">
          {/* Players Header - Large photos with VS centered between circles */}
          <div className="mb-8">
            {/* Row with circles and VS - centered */}
            <div className="flex items-center justify-center space-x-8 mb-4">
              {/* My circle */}
              {myImage ? (
                <img
                  src={myImage}
                  alt={myName}
                  className="w-24 h-24 rounded-full object-cover border-4 border-black"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-gray-200 border-4 border-black flex items-center justify-center">
                  <User size={40} className="text-gray-500" />
                </div>
              )}
              
              {/* VS - centered between circles only */}
              <div className="text-5xl font-black text-black">VS</div>
              
              {/* Opponent circle */}
              {opponentImage ? (
                <img
                  src={opponentImage}
                  alt={opponentName}
                  className="w-24 h-24 rounded-full object-cover border-4 border-gray-400"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-gray-200 border-4 border-gray-400 flex items-center justify-center">
                  <User size={40} className="text-gray-500" />
                </div>
              )}
            </div>
            
            {/* Names row - below circles */}
            <div className="flex items-center justify-center space-x-16">
              <div className="text-center w-24">
                <p className="font-bold text-lg">{myName}</p>
                <p className="text-sm text-gray-500">Tú</p>
              </div>
              <div className="w-16"></div> {/* Spacer for VS area */}
              <div className="text-center w-24">
                <p className="font-bold text-lg">{opponentName}</p>
                <p className="text-sm text-gray-500">Rival</p>
              </div>
            </div>
          </div>

          {/* Game Content */}
          {gamePhase === 'wheel' && (
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-center mb-6">
                Gira la rueda para la Ronda {currentRound + 1}
              </h3>
              
              {/* Wheel with realistic physics */}
              <div className="flex flex-col items-center justify-center py-4">
                <div className="relative w-80 h-80">
                  {/* Pointer pointing DOWN (into the wheel) */}
                  <div 
                    ref={pointerRef}
                    className="absolute -top-4 left-1/2 z-30"
                    style={{
                      transform: 'translateX(-50%) rotate(0deg)',
                      transition: 'transform 0.1s ease-out',
                      transformOrigin: 'center top'
                    }}
                  >
                    <div 
                      style={{
                        width: 0,
                        height: 0,
                        borderLeft: '20px solid transparent',
                        borderRight: '20px solid transparent', 
                        borderTop: '40px solid #dc2626',
                        filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.3))'
                      }}
                    ></div>
                  </div>
                  
                  {/* Wheel */}
                  <div 
                    ref={wheelRef}
                    className="w-full h-full rounded-full border-8 border-gray-800 shadow-2xl relative overflow-hidden"
                    style={{ 
                      transform: `rotate(${wheelRotation}deg)`,
                      background: `conic-gradient(${specialties.map((specialty, index) => 
                        `${specialty.color} ${index * SEGMENT_ANGLE}deg ${(index + 1) * SEGMENT_ANGLE}deg`
                      ).join(', ')})`
                    }}
                  >
                    {/* Materia Labels - radial text (reads from center outward) */}
                    {specialties.map((specialty, index) => {
                      const angle = (index * SEGMENT_ANGLE) + (SEGMENT_ANGLE / 2);
                      return (
                        <div
                          key={specialty.value || index}
                          className="absolute pointer-events-none"
                          style={{
                            left: '50%',
                            top: '50%',
                            width: '44%',
                            height: '14px',
                            transformOrigin: '0% 50%',
                            transform: `rotate(${angle - 90}deg)`,
                          }}
                        >
                          <span
                            className="absolute text-white font-bold"
                            style={{
                              right: '6px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              whiteSpace: 'nowrap',
                              fontSize: specialties.length > 12 ? '8.5px' : '12px',
                              letterSpacing: '0.2px',
                              textShadow: '1px 1px 2px rgba(0,0,0,0.85)',
                            }}
                          >
                            {specialty.name}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Center Button */}
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
                    <button
                      data-testid="spin-wheel-button"
                      onClick={spinWheel}
                      disabled={isSpinning}
                      className={`w-20 h-20 rounded-full border-4 border-white shadow-lg flex items-center justify-center text-white font-bold text-sm transition-all ${
                        isSpinning 
                          ? 'bg-gradient-to-br from-gray-400 to-gray-500 cursor-not-allowed' 
                          : 'bg-gradient-to-br from-yellow-400 to-orange-500 hover:scale-105 cursor-pointer'
                      }`}
                    >
                      {isSpinning ? 'GIRANDO' : 'GIRAR'}
                    </button>
                  </div>
                </div>
                
                {/* Status text */}
                <div className="mt-6 text-center">
                  <p className="text-gray-600 text-lg">
                    {isSpinning 
                      ? '🎯 La rueda está girando...' 
                      : selectedSpecialty 
                        ? `✨ ¡${selectedSpecialty.name}!` 
                        : 'Presiona GIRAR para seleccionar especialidad'
                    }
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Question Phase */}
          {gamePhase === 'question' && currentQuestion && (
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="mb-4">
                <span className="inline-block px-3 py-1 bg-black text-white text-sm font-medium rounded-full">
                  {currentDuel.round_specialties[currentRound]}
                </span>
                {currentQuestion.tema && (
                  <span className="inline-block px-3 py-1 bg-gray-200 text-gray-700 text-sm font-medium rounded-full ml-2">
                    {currentQuestion.tema}
                  </span>
                )}
              </div>
              
              <h2 className="text-xl font-semibold text-gray-900 leading-relaxed mb-6">
                {currentQuestion.question_text}
              </h2>

              <div className="space-y-3">
                {['A', 'B', 'C', 'D'].filter(option => {
                  const optionText = currentQuestion[`option_${option.toLowerCase()}`];
                  return optionText !== undefined && optionText !== null && optionText !== '';
                }).map((option) => {
                  const optionText = currentQuestion[`option_${option.toLowerCase()}`];
                  
                  return (
                    <button
                      key={option}
                      onClick={() => handleAnswerSelect(option)}
                      className="w-full p-4 text-left border-2 border-gray-200 rounded-lg hover:border-black hover:bg-gray-50 transition-all"
                    >
                      <span className="font-bold mr-3">{option}.</span>
                      <span>{optionText}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Feedback Phase */}
          {gamePhase === 'feedback' && currentQuestion && (
            <div className="bg-gray-50 rounded-xl p-6">
              <div className={`p-4 rounded-lg mb-6 ${
                selectedAnswer === currentQuestion.correct_answer 
                  ? 'bg-green-100 border-2 border-green-500' 
                  : 'bg-red-100 border-2 border-red-500'
              }`}>
                <h4 className="text-lg font-bold flex items-center mb-2">
                  {selectedAnswer === currentQuestion.correct_answer ? (
                    <>
                      <Check size={24} className="text-green-600 mr-2" />
                      <span className="text-green-700">¡Correcto!</span>
                    </>
                  ) : (
                    <>
                      <X size={24} className="text-red-600 mr-2" />
                      <span className="text-red-700">Incorrecto</span>
                    </>
                  )}
                </h4>
                <p><strong>Respuesta correcta:</strong> {currentQuestion[`option_${currentQuestion.correct_answer.toLowerCase()}`] || currentQuestion.correct_answer}</p>
              </div>
              
              <div className="bg-white p-4 rounded-lg border mb-6">
                {currentQuestion.explanation ? (
                  <>
                    <p className="mb-2"><strong>Explicación:</strong></p>
                    <p className="text-gray-700">{currentQuestion.explanation}</p>
                  </>
                ) : (
                  <p className="text-gray-500 italic">La respuesta correcta es: <strong>{currentQuestion[`option_${currentQuestion.correct_answer.toLowerCase()}`] || currentQuestion.correct_answer}</strong></p>
                )}
                {currentQuestion.reference && (
                  <p className="mt-2 text-sm text-gray-500">
                    <strong>Referencia:</strong> {currentQuestion.reference}
                  </p>
                )}
              </div>
              
              <button
                onClick={handleNextRound}
                className="w-full px-6 py-3 bg-black text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors"
              >
                {currentRound < 4 ? 'Siguiente Pregunta' : 'Ver Resultados'}
              </button>
            </div>
          )}

          {/* Results Phase */}
          {gamePhase === 'results' && (
            <div className="bg-gray-50 rounded-xl p-8 text-center">
              {/* Final Result Banner - Only when duel is completed */}
              {finalResult && (
                <div className={`mb-8 p-6 rounded-xl ${
                  finalResult === 'win' ? 'bg-green-100 border-4 border-green-500' :
                  finalResult === 'lose' ? 'bg-red-100 border-4 border-red-500' :
                  'bg-yellow-100 border-4 border-yellow-500'
                }`}>
                  <div className="text-7xl mb-4">
                    {finalResult === 'win' ? '🏆' : finalResult === 'lose' ? '😔' : '🤝'}
                  </div>
                  <h2 className={`text-4xl font-black ${
                    finalResult === 'win' ? 'text-green-700' :
                    finalResult === 'lose' ? 'text-red-700' :
                    'text-yellow-700'
                  }`}>
                    {finalResult === 'win' ? '¡GANASTE!' :
                     finalResult === 'lose' ? '¡PERDISTE!' :
                     '¡EMPATE!'}
                  </h2>
                  <div className="mt-4 flex justify-center items-center space-x-6">
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Tú</p>
                      <p className="text-3xl font-bold">{myFinalScore}</p>
                    </div>
                    <span className="text-2xl font-bold text-gray-400">-</span>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">{opponentName}</p>
                      <p className="text-3xl font-bold">{opponentFinalScore}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Show challenger message if this was a received challenge */}
              {receivedChallengerMessage && (
                <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-500 mb-1">Mensaje del retador:</p>
                  <p className="text-gray-800 italic">"{receivedChallengerMessage}"</p>
                </div>
              )}
              
              {/* Winner message input - only show when user wins as player2 (the one who was challenged) */}
              {finalResult === 'win' && !showWinnerMessageInput && currentDuel?.player2_id === user.id && (
                <button
                  onClick={() => setShowWinnerMessageInput(true)}
                  className="mb-6 px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center mx-auto"
                >
                  <MessageCircle size={20} className="mr-2" />
                  Enviar mensaje al retador
                </button>
              )}
              
              {showWinnerMessageInput && (
                <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                    <MessageCircle size={16} className="inline mr-1" />
                    Tu mensaje de victoria para {opponentName}:
                  </label>
                  <textarea
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-green-500 transition-colors resize-none"
                    placeholder="Ej: ¡Mejor suerte la próxima vez! 😎"
                    rows={2}
                    maxLength={200}
                    value={winnerMessage}
                    onChange={(e) => setWinnerMessage(e.target.value)}
                  />
                  <div className="flex justify-between items-center mt-2">
                    <p className="text-xs text-gray-400">{winnerMessage.length}/200 caracteres</p>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setShowWinnerMessageInput(false)}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={sendWinnerMessage}
                        disabled={!winnerMessage.trim()}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400"
                      >
                        Enviar
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Waiting message - Only when duel is NOT completed */}
              {!finalResult && (
                <>
                  <div className="text-6xl mb-4">
                    {myFinalScore >= 4 ? '🏆' : myFinalScore >= 3 ? '👍' : '💪'}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    ¡Has completado el duelo!
                  </h3>
                  <div className={`text-5xl font-bold mb-6 ${
                    myFinalScore >= 4 ? 'text-green-600' : 
                    myFinalScore >= 3 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {myFinalScore}/5
                  </div>
                  <p className="text-gray-600 mb-6">
                    Esperando a que {opponentName} complete sus preguntas...
                  </p>
                </>
              )}
              
              <button
                onClick={exitGame}
                className="px-8 py-3 bg-black text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors"
              >
                Volver a Duelos
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ==================== MAIN DUELS UI ====================
  
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 py-4 px-4 z-50 bg-white">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link
            to="/"
            data-testid="back-to-dashboard"
            className="flex items-center space-x-2 text-black font-medium text-sm hover:text-gray-600 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span>Menú Principal</span>
          </Link>
          
          <button
            onClick={onLogout}
            className="bg-white rounded-full p-1.5 border border-black hover:bg-gray-100 transition-all"
          >
            <span className="text-black text-sm px-2">Salir</span>
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 pt-24 pb-8">
        {/* Title */}
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-black mb-4">
            Duelos
          </h1>
          <p className="text-gray-600">
            Reta a tus compañeros en duelos de 5 preguntas
          </p>
        </div>

        {/* Tabs - Responsive for mobile */}
        <nav className="mb-12">
          <ul className="flex flex-col sm:flex-row justify-center items-center gap-4 sm:gap-8">
            {tabs.map((tab) => (
              <li key={tab.id}>
                <button
                  data-testid={`${tab.id}-tab`}
                  onClick={() => setActiveTab(tab.id)}
                  onMouseEnter={() => setHoveredTab(tab.id)}
                  onMouseLeave={() => setHoveredTab(null)}
                  className={`group flex items-center space-x-2 text-base sm:text-lg font-medium transition-colors duration-300 px-4 py-2 rounded-lg ${
                    activeTab === tab.id 
                      ? 'bg-black text-white' 
                      : 'text-gray-700 hover:text-black hover:bg-gray-100'
                  }`}
                >
                  <span className="text-lg">{tab.icon}</span>
                  <span className="whitespace-nowrap">{tab.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Lobby Tab */}
        {activeTab === 'lobby' && (
          <div className="text-center">
            <h3 className="text-2xl font-bold text-black mb-6">
              Retar a un Compañero
            </h3>
            
            {!showChallengeForm ? (
              <button
                data-testid="create-challenge-button"
                className="px-8 py-3 bg-black text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors"
                onClick={async () => {
                  setShowChallengeForm(true);
                  setShowUserList(true);
                  // Cargar primeros 20 usuarios
                  await fetchActiveUsers('');
                }}
              >
                ⚔️ Retar Usuario
              </button>
            ) : (
              <div className="max-w-md mx-auto">
                <div className="bg-gray-50 rounded-xl p-6">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">
                    Selecciona un usuario
                  </h4>
                  
                  <div className="mb-4 relative">
                    <div 
                      className="relative cursor-pointer"
                      onClick={() => {
                        if (activeUsers.length === 0) {
                          fetchActiveUsers('');
                        }
                        setShowUserList(!showUserList);
                      }}
                    >
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                      <input
                        type="text"
                        data-testid="challenge-username-input"
                        className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-black transition-colors cursor-pointer"
                        placeholder={loadingUsers ? "Cargando usuarios..." : "Haz clic para ver usuarios..."}
                        value={challengeUsername}
                        onChange={(e) => filterUsers(e.target.value)}
                        onFocus={() => {
                          setFilteredUsers(activeUsers);
                          setShowUserList(true);
                        }}
                        readOnly={false}
                        disabled={loadingUsers}
                      />
                      <ChevronDown 
                        className={`absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 transition-transform ${showUserList ? 'rotate-180' : ''}`} 
                        size={20} 
                      />
                    </div>
                    
                    {showUserList && (
                      <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto mt-1">
                        {loadingUsers ? (
                          <div className="p-4 text-gray-500 text-sm text-center">
                            <div className="animate-spin w-5 h-5 border-2 border-gray-300 border-t-black rounded-full mx-auto mb-2"></div>
                            Cargando usuarios...
                          </div>
                        ) : filteredUsers.length > 0 ? (
                          filteredUsers.map((u) => (
                            <div
                              key={u.id}
                              data-testid={`user-option-${u.id}`}
                              className={`p-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0 flex items-center space-x-3 ${
                                selectedUser?.id === u.id ? 'bg-gray-100' : ''
                              }`}
                              onClick={() => selectUser(u)}
                            >
                              {u.profile_image ? (
                                <img src={u.profile_image} alt={u.full_name} className="w-10 h-10 rounded-full object-cover" />
                              ) : (
                                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                                  <User size={20} className="text-gray-500" />
                                </div>
                              )}
                              <div className="text-left">
                                <div className="font-semibold text-sm">{u.full_name}</div>
                                <div className="text-xs text-gray-500">{u.email}</div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="p-4 text-gray-500 text-sm text-center">
                            {challengeUsername ? 'No se encontraron usuarios' : 'No hay usuarios aprobados disponibles'}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {selectedUser && (
                    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
                      <p className="text-sm text-gray-600 mb-2">Usuario seleccionado:</p>
                      <div className="flex items-center space-x-3">
                        {selectedUser.profile_image ? (
                          <img src={selectedUser.profile_image} alt={selectedUser.full_name} className="w-12 h-12 rounded-full object-cover" />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                            <User size={24} className="text-gray-500" />
                          </div>
                        )}
                        <div>
                          <p className="font-semibold">{selectedUser.full_name}</p>
                          <p className="text-sm text-gray-500">{selectedUser.email}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Message input for challenger */}
                  {selectedUser && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <MessageCircle size={16} className="inline mr-1" />
                        Mensaje para {selectedUser.full_name} (opcional)
                      </label>
                      <textarea
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-black transition-colors resize-none"
                        placeholder="Ej: ¡Te voy a ganar!"
                        rows={2}
                        maxLength={200}
                        value={challengerMessage}
                        onChange={(e) => setChallengerMessage(e.target.value)}
                      />
                      <p className="text-xs text-gray-400 mt-1">{challengerMessage.length}/200 caracteres</p>
                    </div>
                  )}
                  
                  {/* Topic selector for duel */}
                  {selectedUser && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Modo de preguntas
                      </label>
                      <button
                        type="button"
                        data-testid="topic-general"
                        onClick={() => setDuelTopic('')}
                        className={`w-full mb-2 px-3 py-2 text-sm rounded-lg border-2 transition-all ${
                          !duelTopic 
                            ? 'border-black bg-black text-white font-semibold' 
                            : 'border-gray-200 text-gray-700 hover:border-gray-400'
                        }`}
                      >
                        Cualquier tema (rueda completa)
                      </button>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-56 overflow-y-auto pr-1">
                        {specialties.map(t => (
                          <button
                            key={t.value}
                            type="button"
                            data-testid={`topic-${t.value}`}
                            onClick={() => setDuelTopic(t.value)}
                            className={`px-2 py-2 text-xs rounded-lg border-2 transition-all flex items-center gap-1.5 ${
                              duelTopic === t.value 
                                ? 'border-black bg-black text-white font-semibold' 
                                : 'border-gray-200 text-gray-700 hover:border-gray-400'
                            }`}
                          >
                            <span
                              className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                              style={{ backgroundColor: t.color }}
                            ></span>
                            <span className="truncate">{t.name}</span>
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        {duelTopic 
                          ? `Las 5 preguntas serán de ${specialties.find(s => s.value === duelTopic)?.name || duelTopic}` 
                          : 'La rueda elegirá una materia distinta por ronda'}
                      </p>
                    </div>
                  )}
                  
                  <div className="flex space-x-4">
                    <button
                      type="button"
                      data-testid="send-challenge-button"
                      className="flex-1 px-6 py-3 bg-black text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors disabled:bg-gray-400"
                      onClick={sendChallenge}
                      disabled={!selectedUser || loading}
                    >
                      {loading ? 'Enviando...' : '⚔️ Enviar Reto'}
                    </button>
                    <button
                      type="button"
                      data-testid="cancel-challenge-button"
                      className="flex-1 px-6 py-3 bg-white text-black font-semibold rounded-lg border border-black hover:bg-gray-100 transition-colors"
                      onClick={resetChallengeForm}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Pending Duels Tab */}
        {activeTab === 'pending' && (
          <div>
            {/* Refresh button */}
            <div className="flex justify-end mb-4">
              <button
                onClick={() => fetchData(true)}
                disabled={loadingData}
                className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-black hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              >
                <RefreshCw size={18} className={loadingData ? 'animate-spin' : ''} />
                <span>{loadingData ? 'Actualizando...' : 'Actualizar'}</span>
              </button>
            </div>
            
            {pendingDuels.length === 0 ? (
              <div className="text-center py-12">
                <Clock size={48} className="mx-auto text-gray-400 mb-4" />
                <h3 className="text-xl font-semibold text-black mb-2">
                  No tienes retos pendientes
                </h3>
                <p className="text-gray-600">
                  Cuando alguien te rete, aparecerá aquí
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingDuels.map((duel) => (
                  <div key={duel.id} className="p-6 border border-gray-300 rounded-lg">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div className="flex items-center space-x-4">
                        {duel.challenger_image ? (
                          <img 
                            src={duel.challenger_image} 
                            alt={duel.challenger_name}
                            className="w-14 h-14 rounded-full object-cover border-2 border-gray-300"
                          />
                        ) : (
                          <div className="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center">
                            <User size={24} className="text-gray-500" />
                          </div>
                        )}
                        <div>
                          <h4 className="text-lg font-semibold text-black">
                            {duel.challenger_name} te ha retado
                          </h4>
                          <p className="text-gray-600">{duel.status_message}</p>
                          {/* Date and time */}
                          {duel.created_at && (
                            <p className="text-xs text-gray-400 mt-1">
                              📅 {new Date(duel.created_at).toLocaleDateString('es-MX', { 
                                day: 'numeric', 
                                month: 'short', 
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          )}
                          {/* Challenger message */}
                          {duel.challenger_message && (
                            <div className="mt-2 p-2 bg-gray-100 rounded-lg">
                              <p className="text-sm italic text-gray-700">
                                💬 "{duel.challenger_message}"
                              </p>
                            </div>
                          )}
                          {/* Duel topic challenge message */}
                          <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <p className="text-sm font-medium text-yellow-800">
                              {duel.duel_topic 
                                ? `¡Te reto a ver qué tanto sabes de ${duel.duel_topic}!`
                                : '¡Te reto a ver qué tanto sabes de todo!'
                              }
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          data-testid={`accept-duel-${duel.id}`}
                          className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
                          onClick={() => acceptDuel(duel)}
                        >
                          ⚔️ Jugar
                        </button>
                        <button
                          data-testid={`reject-duel-${duel.id}`}
                          className="px-4 py-2 bg-white text-black border border-black rounded-lg hover:bg-gray-100 transition-colors"
                          onClick={() => rejectDuel(duel.id)}
                        >
                          Rechazar
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Completed Duels Tab */}
        {activeTab === 'completed' && (
          <div>
            {/* Refresh button */}
            <div className="flex justify-end mb-4">
              <button
                onClick={() => fetchData(true)}
                disabled={loadingData}
                className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-black hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              >
                <RefreshCw size={18} className={loadingData ? 'animate-spin' : ''} />
                <span>{loadingData ? 'Actualizando...' : 'Actualizar'}</span>
              </button>
            </div>
            
            {completedDuels.length === 0 ? (
              <div className="text-center py-12">
                <Trophy size={48} className="mx-auto text-gray-400 mb-4" />
                <h3 className="text-xl font-semibold text-black mb-2">
                  No hay duelos terminados
                </h3>
                <p className="text-gray-600">
                  Tus duelos completados aparecerán aquí
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {completedDuels.map((duel) => (
                  <div key={duel.id} className="p-6 border border-gray-300 rounded-lg">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div className="flex items-center space-x-6">
                        {/* Player 1 (Challenger) */}
                        <div className="text-center">
                          {duel.player1_image ? (
                            <img 
                              src={duel.player1_image} 
                              alt={duel.player1_name}
                              className="w-16 h-16 rounded-full object-cover mx-auto mb-2 border-2 border-gray-300"
                            />
                          ) : (
                            <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center mx-auto mb-2">
                              <User size={28} className="text-gray-500" />
                            </div>
                          )}
                          <p className="text-sm font-medium text-gray-700">{duel.player1_name}</p>
                          <p className="text-2xl font-bold text-black">{duel.player1_score}</p>
                        </div>
                        
                        {/* VS */}
                        <span className="text-3xl font-black text-gray-800 px-4">VS</span>
                        
                        {/* Player 2 */}
                        <div className="text-center">
                          {duel.player2_image ? (
                            <img 
                              src={duel.player2_image} 
                              alt={duel.player2_name}
                              className="w-16 h-16 rounded-full object-cover mx-auto mb-2 border-2 border-gray-300"
                            />
                          ) : (
                            <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center mx-auto mb-2">
                              <User size={28} className="text-gray-500" />
                            </div>
                          )}
                          <p className="text-sm font-medium text-gray-700">{duel.player2_name}</p>
                          <p className="text-2xl font-bold text-black">{duel.player2_score}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${
                          duel.result_class === 'winner' ? 'text-black' : 'text-gray-500'
                        }`}>
                          {duel.result_message}
                        </p>
                      </div>
                    </div>
                    
                    {/* Messages section */}
                    {(duel.challenger_message || duel.winner_message) && (
                      <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                        {/* Challenger message */}
                        {duel.challenger_message && (
                          <div className="flex items-start space-x-2">
                            <MessageCircle size={16} className="text-blue-500 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-xs text-gray-500 font-medium">{duel.player1_name} (retador):</p>
                              <p className="text-sm text-gray-700 italic">"{duel.challenger_message}"</p>
                            </div>
                          </div>
                        )}
                        
                        {/* Winner reply message */}
                        {duel.winner_message && (
                          <div className="flex items-start space-x-2">
                            <Trophy size={16} className="text-yellow-500 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-xs text-gray-500 font-medium">
                                {duel.winner_id === duel.player2_id ? duel.player2_name : duel.player1_name} (ganador):
                              </p>
                              <p className="text-sm text-gray-700 italic">"{duel.winner_message}"</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DuelMode;
